export function generateBackgroundPlaneShaders() {
  const backgroundShaderMaterialVertexShader = `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `;

  const backgroundShaderMaterialFragmentShader = `
    precision highp float;

    uniform sampler2D u_positionTexture;
    uniform sampler2D u_velocityTexture;
    uniform sampler2D u_originalPositionTexture;

    uniform float u_delta;
    uniform float u_randomSeed;
    uniform float u_resetChance;
    uniform float u_motionScale;

    uniform float u_minLonVelocity;
    uniform float u_maxLonVelocity;
    uniform float u_minLatVelocity;
    uniform float u_maxLatVelocity;

    uniform vec3 u_viewableCircleMidpoint;
    uniform float u_viewableCircleRadius;

    varying vec2 vUv;

    float rand(const vec2 co) {
      float t = dot(vec2(12.9898, 78.233), co);
      return fract(sin(t) * (4375.85453 + t));
    }

    void main() {
      const float PI = 3.141592653589793;
      vec4 encodedPosition = texture2D(u_positionTexture, vUv);
      vec3 positionOnSphere = vec3(
        ((encodedPosition.x * 2.0) - 1.0),
        ((encodedPosition.y * 2.0) - 1.0),
        ((encodedPosition.z * 2.0) - 1.0)
      );

      float lon = atan(positionOnSphere.z, positionOnSphere.x);
      float lat = asin(positionOnSphere.y);
      vec2 lonLat = vec2(
        ((lon + PI) / (2.0 * PI)),
        ((lat + (PI * 0.5)) / PI)
      );
      lonLat.x = mod(lonLat.x, 1.0);
      lonLat.y = clamp(lonLat.y, 0.0, 1.0);

      vec4 velocity = texture2D(u_velocityTexture, lonLat);
      float lonVelocity = (velocity.x * (u_maxLonVelocity - u_minLonVelocity)) + u_minLonVelocity;
      float latVelocity = (velocity.y * (u_maxLatVelocity - u_minLatVelocity)) + u_minLatVelocity;

      vec2 seed = (encodedPosition.xy + velocity.xy) * u_randomSeed;
      float randomReset = step(u_resetChance, rand(seed));

      float lonMask = 1.0 - step(0.2, abs(lonVelocity));
      float latMask = 1.0 - step(0.1, abs(latVelocity));
      float slowReset = lonMask * latMask;
      float reset = min(slowReset + randomReset, 1.0);

      vec3 east = vec3(-sin(lon), 0.0, cos(lon));
      vec3 north = vec3(
        -sin(lat) * cos(lon),
        cos(lat),
        -sin(lat) * sin(lon)
      );
      vec3 tangentVelocity = (east * lonVelocity) + (north * latVelocity);
      float tangentSpeed = length(tangentVelocity);
      vec3 advectedPosition = normalize(positionOnSphere);

      if (tangentSpeed > 0.0) {
        float arcStep = tangentSpeed * u_delta * u_motionScale;
        vec3 tangentDirection = tangentVelocity / tangentSpeed;
        advectedPosition = normalize(
          (advectedPosition * cos(arcStep)) + (tangentDirection * sin(arcStep))
        );
      }

      vec3 originalParticlePosition = texture2D(u_originalPositionTexture, vUv).xyz;
      vec3 finalPosition = mix((advectedPosition + 1.0) * 0.5, originalParticlePosition, reset);

      gl_FragColor = vec4(finalPosition, 1.0);
    }
  `;

  return { backgroundShaderMaterialVertexShader, backgroundShaderMaterialFragmentShader };
}

export function generatePlanetMaterialShaders() {
  const planetMaterialVertexShader = `
    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vViewDirection;

    void main() {
      vUv = uv;
      vNormal = normalize(normalMatrix * normal);

      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      vViewDirection = normalize(-mvPosition.xyz);

      gl_Position = projectionMatrix * mvPosition;
    }
  `;

  const planetMaterialFragmentShader = `
    precision highp float;

    uniform sampler2D u_planetTexture;
    uniform float u_colourScheme;

    varying vec2 vUv;
    varying vec3 vNormal;
    varying vec3 vViewDirection;

    void main() {
      vec2 sampleUv = vec2(fract(vUv.x), clamp(vUv.y, 0.001, 0.999));
      vec3 baseColor = texture2D(u_planetTexture, sampleUv).rgb;

      vec3 lightDirection = normalize(vec3(0.45, 0.35, 1.0));
      float diffuse = 0.42 + (0.58 * max(dot(normalize(vNormal), lightDirection), 0.0));
      float fresnel = pow(1.0 - max(dot(normalize(vNormal), normalize(vViewDirection)), 0.0), 3.0);
      vec3 atmosphere = mix(
        vec3(0.12, 0.33, 0.78),
        vec3(0.31, 0.44, 0.78),
        step(0.5, u_colourScheme)
      ) * fresnel * 0.3;

      gl_FragColor = vec4((baseColor * diffuse) + atmosphere, 1.0);
    }
  `;

  return { planetMaterialFragmentShader, planetMaterialVertexShader };
}

export function generateParticleShaders() {
  const particleVertexShader = `
    precision highp float;

    uniform sampler2D u_positionTexture;
    uniform sampler2D u_velocityTexture;
    uniform sampler2D u_velocityGradientTexture;

    attribute vec2 u_squares;

    varying vec3 vColour;
    varying float vAlpha;

    uniform float u_minMagVelocity;
    uniform float u_maxMagVelocity;
    uniform float u_minLonVelocity;
    uniform float u_maxLonVelocity;
    uniform float u_minLatVelocity;
    uniform float u_maxLatVelocity;

    void main() {
      const float PI = 3.141592653589793;
      vec3 encodedPosition = texture2D(u_positionTexture, u_squares).xyz;
      vec3 positionOnSphere = (encodedPosition * 2.0) - 1.0;

      float lon = atan(positionOnSphere.z, positionOnSphere.x);
      float lat = asin(positionOnSphere.y);
      vec2 lonLat = vec2(
        ((lon + PI) / (2.0 * PI)),
        ((lat + (PI * 0.5)) / PI)
      );
      lonLat = clamp(lonLat, vec2(0.0), vec2(1.0));

      vec4 velocity = texture2D(u_velocityTexture, lonLat);
      float lonVelocity = (velocity.x * (u_maxLonVelocity - u_minLonVelocity)) + u_minLonVelocity;
      float latVelocity = (velocity.y * (u_maxLatVelocity - u_minLatVelocity)) + u_minLatVelocity;
      float magnitudeValue = length(vec2(lonVelocity, latVelocity));
      float magnitudeRange = max(u_maxMagVelocity - u_minMagVelocity, 0.0001);
      float magnitude = clamp((magnitudeValue - u_minMagVelocity) / magnitudeRange, 0.0, 1.0);
      float highSpeed = smoothstep(0.45, 0.9, magnitude);

      vColour = mix(
        texture2D(u_velocityGradientTexture, vec2(magnitude, 0.5)).rgb,
        vec3(1.0),
        0.22
      );
      vAlpha = mix(0.42, 0.11, highSpeed);
      gl_Position = projectionMatrix * modelViewMatrix * vec4(positionOnSphere, 1.0);
      gl_PointSize = mix(2.9, 1.2, highSpeed);
    }
  `;

  const particleFragmentShader = `
    precision highp float;

    varying vec3 vColour;
    varying float vAlpha;

    void main() {
      vec2 centered = gl_PointCoord - vec2(0.5);
      float distSquared = dot(centered, centered);

      if (distSquared > 0.25) {
        discard;
      }

      float falloff = smoothstep(0.25, 0.0, distSquared);
      gl_FragColor = vec4(vColour, vAlpha * falloff);
    }
  `;

  return { particleVertexShader, particleFragmentShader };
}
